import React from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { imageUrl, imageSrcSet } from '@/lib/images'

export interface SearchOption {
  value: string
  label: string
  /** A second line (an e-mail's subject, a course's date). */
  description?: string
  /** Small text on the right ("usado em 3", "pt-BR · en-US"). */
  meta?: string
  /** More words the search matches (synonyms, the subject). */
  keywords?: string
  /** Options with a group are listed under its heading, in the order given. */
  group?: string
  /** A 32 px picture beside the label (a badge, a cover). */
  image?: string
}

const OptionImage = ({ src }: { src?: string }) =>
  src ? <img src={imageUrl(src, { w: 32, h: 32, fit: 'max' }) || undefined} srcSet={imageSrcSet(src, { w: 32, h: 32, fit: 'max' })} alt="" className="h-8 w-8 flex-shrink-0 rounded-md bg-gray-50 object-contain" loading="lazy" /> : null

interface SearchSelectProps {
  value: string | null | undefined
  onChange: (value: string) => void
  options: SearchOption[]
  placeholder: string
  searchPlaceholder: string
  emptyLabel: string
  /** An empty choice on top ("Qualquer curso"); left out when one is required. */
  clearLabel?: string
  /** Rows under the list (create, edit): they close the list when clicked. */
  footer?: (close: () => void) => React.ReactNode
  /** A preview of the highlighted option beside the list (an e-mail's thumbnail); the list widens for it. */
  aside?: (option: SearchOption) => React.ReactNode
  disabled?: boolean
  loading?: boolean
  id?: string
  testId?: string
  className?: string
  'aria-label'?: string
}

const fold = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()

/**
 * One choice from a long list, searchable: a button that shows the choice and
 * opens a list with a search on top. Arrows and Enter pick; each option can
 * carry a second line and a note on the right. For one choice among many
 * (an e-mail, a course, a product); several choices with chips are the
 * members' `ChipPicker`.
 */
export const SearchSelect = ({
  value, onChange, options, placeholder, searchPlaceholder, emptyLabel, clearLabel, footer, aside, disabled, loading, id, testId, className, ...rest
}: SearchSelectProps) => {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [active, setActive] = React.useState(0)
  const listRef = React.useRef<HTMLDivElement>(null)
  const chosen = options.find((o) => o.value === value)
  const q = fold(query)
  const visible = options.filter((o) => !q || fold(`${o.label} ${o.description ?? ''} ${o.keywords ?? ''}`).includes(q))
  const rows: Array<SearchOption | null> = clearLabel && !q ? [null, ...visible] : visible
  React.useEffect(() => { setActive(0) }, [query, open])
  const close = () => { setOpen(false); setQuery('') }
  const pick = (o: SearchOption | null) => { onChange(o ? o.value : ''); close() }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery('') }}>
      <PopoverTrigger asChild>
        <button type="button" id={id} disabled={disabled} aria-label={rest['aria-label']} data-testid={testId} aria-expanded={open}
          className={cn('flex h-auto min-h-9 w-full min-w-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-left text-sm hover:border-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50', className)}>
          {chosen && <OptionImage src={chosen.image} />}
          <span className="min-w-0 flex-1">
            {chosen ? (
              <>
                <span className="block truncate text-gray-900">{chosen.label}</span>
                {chosen.description && <span className="block truncate text-xs text-gray-500">{chosen.description}</span>}
              </>
            ) : <span className="block truncate text-gray-500">{value ? '…' : (clearLabel ?? placeholder)}</span>}
          </span>
          <ChevronsUpDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('min-w-[280px] p-0', aside ? 'w-[min(640px,calc(100vw-2rem))]' : 'w-[var(--radix-popover-trigger-width)]')} onOpenAutoFocus={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement | null)?.querySelector('input')?.focus() }}>
        <div className="border-b border-gray-100 p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault()
                  if (!rows.length) return
                  const next = (active + (e.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length
                  setActive(next)
                  listRef.current?.querySelector(`[data-row="${next}"]`)?.scrollIntoView({ block: 'nearest' })
                } else if (e.key === 'Enter') {
                  e.preventDefault()
                  if (rows[active] !== undefined) pick(rows[active])
                }
              }}
              className="h-9 w-full rounded-md border border-gray-300 pl-8 pr-2 text-sm outline-none focus:border-primary" />
          </div>
        </div>
        <div className={cn(aside && 'flex')}>
        <div ref={listRef} role="listbox" className={cn('max-h-72 overflow-y-auto p-1 [scrollbar-width:thin]', aside && 'w-1/2 min-w-0 flex-shrink-0')}>
          {loading && <p className="px-2 py-3 text-xs text-gray-500">…</p>}
          {!loading && !rows.length && <p className="px-2 py-3 text-xs text-gray-500">{emptyLabel}</p>}
          {rows.map((o, i) => (
            <React.Fragment key={o?.value ?? '__none'}>
            {o?.group && o.group !== rows[i - 1]?.group && <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{o.group}</p>}
            <button type="button" role="option" aria-selected={o ? o.value === value : !value} data-row={i}
              onMouseEnter={() => setActive(i)} onClick={() => pick(o)}
              className={cn('flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm', i === active && 'bg-gray-100', o?.image && 'items-center')}>
              {o && <OptionImage src={o.image} />}
              <span className="min-w-0 flex-1">
                <span className={cn('block truncate', o ? 'text-gray-900' : 'text-gray-600')}>{o ? o.label : clearLabel}</span>
                {o?.description && <span className="block truncate text-xs text-gray-500">{o.description}</span>}
              </span>
              {o?.meta && <span className="flex-shrink-0 pt-0.5 text-[11px] text-gray-500">{o.meta}</span>}
              {(o ? o.value === value : !value) && <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />}
            </button>
            </React.Fragment>
          ))}
        </div>
        {aside && <div className="min-w-0 flex-1 border-l border-gray-100 p-2" data-testid={testId ? `${testId}-aside` : undefined}>{rows[active] ? aside(rows[active] as SearchOption) : null}</div>}
        </div>
        {footer && <div className="border-t border-gray-100 p-1">{footer(close)}</div>}
      </PopoverContent>
    </Popover>
  )
}
